// The puzzle card's row of dots, one per puzzle of the game, and its line
// about the game's own move once the puzzle is over. Pure, so they are
// tested without React; the container in `index.tsx` passes them to
// `PuzzleCard`.
import type { Attempts } from './files.ts';
import type { Puzzle } from './finder.ts';
import { attemptAt, seriesWith } from './series.ts';

export type DotState = 'solved' | 'current' | 'pending';

export interface Dot {
  id: string;
  /** 0-based, as the puzzle has it. */
  ply: number;
  state: DotState;
}

/**
 * One dot per puzzle of the game's series (series.ts, the rows the stage
 * panels list), in ply order: green for one the reader has solved, the
 * accent for the one on the board, muted otherwise. The opening's theory
 * puzzle is not a dot: its card shows the series with none current.
 */
export function dotsOf(series: readonly number[], current: Puzzle, attempts: Attempts['attempts']): Dot[] {
  const all = current.kind === 'theory' ? [...series] : seriesWith(series, current.ply);
  return all.map((ply) => ({
    id: `ply:${ply}`,
    ply,
    state: current.kind !== 'theory' && ply === current.ply ? 'current' : attemptAt(attempts, ply) === 'solved' ? 'solved' : 'pending',
  }));
}

/** What a dot says when hovered. */
export function dotTitle(dot: Dot): string {
  const move = `${Math.floor(dot.ply / 2) + 1}${dot.ply % 2 === 0 ? '.' : '...'}`;
  return dot.state === 'current' ? `This puzzle · move ${move}` : dot.state === 'solved' ? `Solved · move ${move}` : `Not yet solved · move ${move}`;
}

/**
 * "In the game you played Nf3, which cost 2.3 pawns": the reader's own move
 * at the puzzle's ply, in SAN, and what the scan says it gave up. Nothing
 * without the move, and no cost when the scan recorded none.
 */
export function playedLine(puzzle: Puzzle, playedSan: string | undefined): string | undefined {
  if (!playedSan || puzzle.kind === 'theory') return undefined;
  const pawns = puzzle.swing / 100;
  if (puzzle.swing >= 5000) return `In the game you played ${playedSan}, which walked into a mate.`;
  if (pawns < 0.05) return `In the game you played ${playedSan}.`;
  return `In the game you played ${playedSan}, which cost ${pawns.toFixed(1)} ${pawns.toFixed(1) === '1.0' ? 'pawn' : 'pawns'}.`;
}
